import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authAPI, marketplaceAPI, pointsAPI, mediaAPI, resolveMediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EQUIPMENT_FIELDS } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { ImagePlus } from "lucide-react";
import ProductSelector from "@/components/ProductSelector";
import ProfileSlugCard from "@/components/ProfileSlugCard";
import ProfileAchievements from "@/components/ProfileAchievements";
import { memberCategoryLabel, memberCategoryBadgeClass } from "@/lib/memberCategory";
import {
  Loader2, Trophy, ShoppingBag, Star, Wrench, Trash2, Plus, MapPin, Pencil,
  Gauge, Users, Package, Bookmark, Award, ShieldCheck, ChevronRight, MessagesSquare,
} from "lucide-react";

function StatCard({ icon: Icon, value, label, to }) {
  const Comp = to ? Link : "div";
  return (
    <Comp to={to} className="block">
      <Card className="border-border bg-card p-4 text-center transition-colors hover:border-primary/40">
        <Icon className="mx-auto mb-1 h-5 w-5 text-primary" />
        <p className="font-display text-xl font-bold">{value}</p>
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      </Card>
    </Comp>
  );
}

function QuickAction({ icon: Icon, label, to }) {
  return (
    <Link to={to} className="group flex flex-1 flex-col items-center gap-1.5 rounded-lg border border-border bg-card p-3 transition-colors hover:border-primary/50">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground group-hover:text-foreground">{label}</span>
    </Link>
  );
}

function ActionRow({ icon: Icon, label, to, testId }) {
  return (
    <Link to={to} className="flex items-center gap-3 border-b border-border px-4 py-3 last:border-b-0 hover:bg-secondary/40" data-testid={testId}>
      <Icon className="h-4 w-4 text-primary" />
      <span className="flex-1 text-sm font-medium">{label}</span>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </Link>
  );
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    bio: user?.bio || "",
    location: user?.location || "",
    profile_image: user?.profile_image || "",
    cover_image: user?.cover_image || "",
    equipment: {
      wheel_base: user?.equipment?.wheel_base || "",
      steering_wheel: user?.equipment?.steering_wheel || "",
      pedals: user?.equipment?.pedals || "",
      shifter: user?.equipment?.shifter || "",
      handbrake: user?.equipment?.handbrake || "",
      cockpit: user?.equipment?.cockpit || "",
      motion_system: user?.equipment?.motion_system || "",
    },
  });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  const initSetup = () => {
    const eq = user?.equipment || {};
    const s = eq.setup || {};
    const out = {};
    EQUIPMENT_FIELDS.forEach((f) => {
      const item = s[f.key];
      out[f.key] = item && (item.manufacturer || item.product)
        ? { manufacturer: item.manufacturer || "", product: item.product || "" }
        : { manufacturer: "", product: eq[f.key] || "" };
    });
    return out;
  };
  const initAccessories = () =>
    (user?.equipment?.accessories || []).map((a) =>
      typeof a === "string" ? { manufacturer: "", product: a } : { manufacturer: a.manufacturer || "", product: a.product || "" }
    );

  const [setupForm, setSetupForm] = useState(initSetup);
  const [accessories, setAccessories] = useState(initAccessories);
  const [accDraft, setAccDraft] = useState({ manufacturer: "", product: "" });
  const [editingSetup, setEditingSetup] = useState(false);

  const [myListingsCount, setMyListingsCount] = useState(0);
  const [pointsHistory, setPointsHistory] = useState([]);

  useEffect(() => {
    marketplaceAPI.getMyListings().then((r) => {
      const arr = Array.isArray(r.data) ? r.data : r.data?.listings || [];
      setMyListingsCount(arr.filter((l) => l.status !== "sold" && l.status !== "deleted").length);
    }).catch(() => {});
    pointsAPI.getHistory({ limit: 5 }).then((r) => {
      setPointsHistory(r.data?.history || r.data?.transactions || []);
    }).catch(() => {});
  }, []);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickAvatar = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const r = new FileReader();
    r.onload = (ev) => setForm((f) => ({ ...f, profile_image: ev.target.result }));
    r.readAsDataURL(file);
  };

  const buildEquipment = () => {
    const equipment = { setup: {}, accessories: accessories.filter((a) => a.product || a.manufacturer) };
    EQUIPMENT_FIELDS.forEach((f) => {
      const v = setupForm[f.key] || { manufacturer: "", product: "" };
      equipment.setup[f.key] = { manufacturer: v.manufacturer || "", product: v.product || "" };
      equipment[f.key] = `${v.manufacturer || ""} ${v.product || ""}`.trim();
    });
    return equipment;
  };

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = { ...form, equipment: buildEquipment() };
      const res = await authAPI.updateProfile(payload);
      setUser(res.data);
      localStorage.setItem("user", JSON.stringify(res.data));
      setEditing(false);
      setEditingSetup(false);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update profile");
    } finally {
      setSaving(false);
    }
  };

  const setCategory = (key) => (v) => setSetupForm((s) => ({ ...s, [key]: v }));
  const addAccessory = () => {
    if (!accDraft.product && !accDraft.manufacturer) { toast.error("Pick an item from the catalogue or type your own"); return; }
    setAccessories((a) => [...a, accDraft]);
    setAccDraft({ manufacturer: "", product: "" });
  };
  const removeAccessory = (i) => setAccessories((a) => a.filter((_, idx) => idx !== i));

  const coverRef = useRef(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const r = await mediaAPI.upload(file);
      const url = r.data.url;
      const res = await authAPI.updateProfile({ cover_image: url });
      setUser(res.data);
      localStorage.setItem("user", JSON.stringify(res.data));
      setForm((f) => ({ ...f, cover_image: url }));
      toast.success("Cover image updated");
    } catch {
      toast.error("Could not upload cover image");
    } finally {
      setCoverUploading(false);
    }
  };
  const cover = resolveMediaUrl(form.cover_image || user?.cover_image || "");

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="profile-page">
      {/* Header banner */}
      <section className="relative min-h-[220px] overflow-hidden rounded-xl border border-border">
        {cover ? (
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${cover})` }} />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-zinc-800 via-zinc-900 to-black" />
        )}
        {/* 20% dark overlay */}
        <div className="absolute inset-0 bg-black/20" />
        {/* heavy bottom fade for legibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />

        <input type="file" accept="image/*" ref={coverRef} className="hidden" onChange={uploadCover} data-testid="profile-cover-input" />
        <button
          type="button"
          onClick={() => coverRef.current?.click()}
          disabled={coverUploading}
          className="absolute right-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full border border-white/30 bg-black/40 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur transition-colors hover:border-primary hover:text-primary"
          data-testid="profile-cover-upload"
        >
          <ImagePlus className="h-3.5 w-3.5" /> {coverUploading ? "Uploading…" : cover ? "Change cover" : "Add cover"}
        </button>

        <div className="relative flex items-end gap-4 p-6 pt-24">
          <div className="relative">
            <Avatar className="h-20 w-20 border-2 border-primary">
              <AvatarImage src={form.profile_image} />
              <AvatarFallback className="bg-secondary text-2xl">{(user?.username || "U").slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-display text-2xl font-bold uppercase">{user?.username}</h1>
            <p className="truncate text-sm text-muted-foreground">{user?.email}</p>
            {(user?.member_category && memberCategoryLabel(user.member_category)) || user?.is_founding_member ? (
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {user?.member_category && memberCategoryLabel(user.member_category) && (
                  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${memberCategoryBadgeClass(user.member_category)}`} data-testid="profile-member-badge">
                    {memberCategoryLabel(user.member_category)}
                  </span>
                )}
                {user?.is_founding_member && user?.member_category !== "founding_member" && (
                  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${memberCategoryBadgeClass("founding_member")}`} data-testid="profile-founding-badge">
                    Founding Member
                  </span>
                )}
              </div>
            ) : null}
            {user?.location && (
              <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                <MapPin className="h-3 w-3" /> {user.location}
              </p>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => setEditing((v) => !v)} data-testid="profile-edit-toggle">
            <Pencil className="mr-1 h-3.5 w-3.5" /> {editing ? "Close" : "Edit profile"}
          </Button>
        </div>
        {user?.bio && !editing && <p className="relative px-6 pb-5 text-sm text-muted-foreground">{user.bio}</p>}
      </section>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard icon={Trophy} value={user?.points ?? 0} label="Points" to="/points" />
        <StatCard icon={ShoppingBag} value={myListingsCount} label="Listings" to="/my-listings" />
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-4 gap-3">
        <QuickAction icon={ShoppingBag} label="My Listings" to="/my-listings" />
        <QuickAction icon={Bookmark} label="Saved" to="/saved" />
        <QuickAction icon={Package} label="Orders" to="/orders" />
        <QuickAction icon={MessagesSquare} label="Messages" to="/messages" />
      </div>

      {/* Custom URL / slug */}
      <ProfileSlugCard />

      {/* Editable form */}
      {editing && (
        <Card className="border-border bg-card p-5" data-testid="profile-edit-card">
          <h2 className="mb-4 font-display text-lg font-semibold uppercase">Edit profile</h2>
          <form onSubmit={save} className="space-y-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-14 w-14">
                <AvatarImage src={form.profile_image} />
                <AvatarFallback className="bg-secondary">{(user?.username || "U").slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <label className="cursor-pointer text-xs font-semibold text-primary hover:underline" data-testid="profile-avatar-picker">
                Change photo
                <input type="file" accept="image/*" className="hidden" onChange={pickAvatar} />
              </label>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>First name</Label>
                <Input value={form.first_name} onChange={upd("first_name")} data-testid="profile-firstname-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Last name</Label>
                <Input value={form.last_name} onChange={upd("last_name")} data-testid="profile-lastname-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Location</Label>
              <Input value={form.location} onChange={upd("location")} placeholder="City, Country" data-testid="profile-location-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Bio</Label>
              <Textarea rows={3} value={form.bio} onChange={upd("bio")} data-testid="profile-bio-input" />
            </div>

            <Button type="submit" disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="profile-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
            </Button>
          </form>
        </Card>
      )}

      {/* SimHub + Clubs */}
      <Card className="overflow-hidden border-border bg-card p-0">
        <ActionRow icon={ShieldCheck} label="SimHub Dashboard" to="/simhub" testId="profile-simhub-link" />
        <ActionRow icon={Users} label="Racing Clubs" to="/clubs" testId="profile-clubs-link" />
        <ActionRow icon={Award} label="Sponsorship" to="/sponsorship" testId="profile-sponsorship-link" />
      </Card>

      {/* MY SETUP - display equipment values */}
      <ProfileAchievements selfView />

      <Card className="border-border bg-card p-5" data-testid="profile-setup-card">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold uppercase">
            <Wrench className="h-4 w-4 text-primary" /> My Setup
          </h2>
          {editingSetup ? (
            <div className="flex gap-3">
              <button onClick={() => { setSetupForm(initSetup()); setAccessories(initAccessories()); setEditingSetup(false); }} className="text-xs font-semibold text-muted-foreground hover:underline" data-testid="profile-setup-cancel">CANCEL</button>
              <button onClick={() => save()} disabled={saving} className="text-xs font-semibold text-primary hover:underline" data-testid="profile-setup-save">{saving ? "SAVING…" : "SAVE"}</button>
            </div>
          ) : (
            <button onClick={() => setEditingSetup(true)} className="text-xs font-semibold text-primary hover:underline" data-testid="profile-setup-edit">EDIT</button>
          )}
        </div>

        {editingSetup ? (
          <div className="space-y-4">
            {EQUIPMENT_FIELDS.map((f) => (
              <div key={f.key} className="rounded-md border border-border p-3">
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">{f.label}</p>
                <ProductSelector
                  manufacturer={setupForm[f.key]?.manufacturer || ""}
                  product={setupForm[f.key]?.product || ""}
                  onChange={setCategory(f.key)}
                  testIdPrefix={`setup-${f.key}`}
                />
              </div>
            ))}

            <div className="rounded-md border border-dashed border-border p-3">
              <p className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">Accessories &amp; Add-ons</p>
              <p className="mb-3 text-[11px] text-muted-foreground">Pick from the catalogue or choose Other to type a manufacturer and product.</p>
              {accessories.length > 0 && (
                <div className="mb-3 space-y-2">
                  {accessories.map((a, i) => (
                    <div key={i} className="flex items-center justify-between rounded-md border border-border bg-secondary/30 px-3 py-2" data-testid={`setup-accessory-${i}`}>
                      <div>
                        <span className="font-medium">{a.product || "—"}</span>
                        {a.manufacturer && <span className="ml-2 text-xs text-muted-foreground">{a.manufacturer}</span>}
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => removeAccessory(i)} data-testid={`setup-accessory-remove-${i}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <ProductSelector
                manufacturer={accDraft.manufacturer}
                product={accDraft.product}
                onChange={setAccDraft}
                testIdPrefix="setup-accessory-draft"
              />
              <Button onClick={addAccessory} className="mt-3 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="setup-accessory-add">
                <Plus className="mr-1 h-4 w-4" /> Add accessory
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="divide-y divide-border rounded-md border border-border">
              {EQUIPMENT_FIELDS.map((f) => (
                <div key={f.key} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <span className="text-muted-foreground">{f.label}</span>
                  <span className={user?.equipment?.[f.key] ? "font-medium" : "text-muted-foreground/60"}>{user?.equipment?.[f.key] || "Not set"}</span>
                </div>
              ))}
            </div>
            {initAccessories().length > 0 && (
              <div className="mt-4">
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Accessories &amp; Add-ons</p>
                <div className="flex flex-wrap gap-2">
                  {initAccessories().map((a, i) => (
                    <span key={i} className="rounded-full border border-border bg-secondary/40 px-3 py-1 text-xs" data-testid={`setup-accessory-chip-${i}`}>
                      {a.product}{a.manufacturer ? ` · ${a.manufacturer}` : ""}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Recent points */}
      <Card className="border-border bg-card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold uppercase">Recent Points</h2>
          <Link to="/points" className="text-xs font-semibold text-primary hover:underline">VIEW ALL</Link>
        </div>
        {pointsHistory.length === 0 ? (
          <p className="text-sm text-muted-foreground">Start engaging to earn points!</p>
        ) : (
          <div className="space-y-2">
            {pointsHistory.slice(0, 5).map((t) => (
              <div key={t.id} className="flex items-center justify-between rounded-md bg-secondary/30 px-3 py-2 text-sm">
                <span className="truncate text-muted-foreground">{t.description || t.action}</span>
                <span className={`font-semibold ${t.points > 0 ? "text-[hsl(145,70%,50%)]" : "text-primary"}`}>
                  {t.points > 0 ? "+" : ""}{t.points}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
